import { reactive, toRaw } from "vue";
import type { IGroup, IScope, IRule } from '@/types/filterBuilderTypes';
import { isGroup, isRule } from "@/types/typeguards/QueryBuilder";
import { deepClone } from "@/utils/object";
import type { FavoriteRuleSnapshot } from "@/utils/favoriteFilters";

let nodesCreated = 0;

/**
 * A new id for a filter row or group. The counter keeps ids unique within this page load. The
 * random part keeps them apart from ids in saved filters made on an earlier load.
 */
export function newFilterNodeId(): string {
    nodesCreated += 1;
    return `${nodesCreated.toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useQueryBuilder() {
    const rawQuery = reactive<IScope>({
        id: 'root',
        scopeType: 'and',
        rules: []
    });

    /** Appends an empty row to the group and returns the row's id, or null when there is no such group. */
    function addRule(groupId?: string): string | null {
        let group;
        if (!groupId) {
            group = rawQuery;
        } else {
            group = findScopeById([rawQuery], groupId);
        }

        if (!group || !isGroup(group)) {
            return null;
        }

        const id = newFilterNodeId();
        group.rules.push({
            id,
            column: '',
            criterion: '',
            value: '',
            field: null,
        });
        return id;
    }

    /**
     * Appends a row pre-filled from a favorite snapshot (SAU-260) — shaped
     * like `addRule`, but with the favorite's column/criterion/value. The row
     * gets a fresh id and a deep-cloned value so it can never alias the
     * stored favorite.
     */
    function addFavoriteRule(groupId: string | undefined, favoriteRule: FavoriteRuleSnapshot) {
        let group;
        if (!groupId) {
            group = rawQuery;
        } else {
            group = findScopeById([rawQuery], groupId);
        }

        if (!group || !isGroup(group)) {
            return;
        }

        group.rules.push({
            id: newFilterNodeId(),
            column: favoriteRule.column,
            criterion: favoriteRule.criterion,
            value: deepClone(favoriteRule.value ?? null),
            field: favoriteRule.field ?? null,
        });
    }

    function findScopeById(rules: IScope[], id: string): IScope | null {
        for (const rule of rules) {
            if (rule.id === id) {
                return rule;
            }
            if (isGroup(rule)) {
                const found = findScopeById(rule.rules, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }


    function addInnerRule(ruleId: string,) {
        const rule = findScopeById([rawQuery], ruleId);

        if (!rule || !isRule(rule)) {
            return;
        }

        const copiedRule = createFromRule(rule);

        const group = rule as any;
        delete group.column;
        delete group.field;
        delete group.criterion;
        delete group.value;
        group.scopeType = 'and';
        group.rules = [copiedRule, createRule()];
    }

    function createFromRule(rule: IRule) {
        return {
            id: newFilterNodeId(),
            column: rule.column,
            field: rule.field,
            criterion: rule.criterion,
            value: rule.value,
        };
    }

    function createRule() {
        return {
            id: newFilterNodeId(),
            column: '',
            criterion: '',
            value: ''
        };
    }

    function deleteRuleById(scope: IScope, id: string): boolean {
        if (!isGroup(scope)) {
            return false;
        }

        const ruleIndex = scope.rules.findIndex(rule => rule.id === id);
        if (ruleIndex !== -1) {
            scope.rules.splice(ruleIndex, 1);
            return true;
        }

        for (const group of scope.rules) {
            if (isGroup(group)) {
                const deleted = deleteRuleById(group, id);
                if (deleted) {
                    if (group.rules.length === 1) {
                        const singleRule = group.rules[0];
                        if (isRule(singleRule)) {
                            convertGroupToRule(group, singleRule);
                        } else {
                            splitGroupToRules(scope, group);
                        }
                    }
                    return true;
                }
            }
        }

        return false;
    }

    function convertGroupToRule(group: IGroup, rule: IRule) {
        const newRule = group as any;

        newRule.column = rule.column;
        newRule.field = rule.field;
        newRule.criterion = rule.criterion;
        newRule.value = rule.value;
        delete newRule.rules;
        delete newRule.scopeType;
    }

    function splitGroupToRules(parentGroup: IGroup, group: IGroup) {
        parentGroup.rules.splice(parentGroup.rules.indexOf(group), 1);
        parentGroup.rules.push(...group.rules);
    }

    function deleteRule(ruleId: string) {
        deleteRuleById(rawQuery, ruleId);
    }

    function findSingleChildGroups(scope: IScope) {
        if (isRule(scope)) {
            return false;
        }

        if (scope.rules.length === 1) {
            if (isRule(scope.rules[0])) {
                convertGroupToRule(scope, scope.rules[0] as IRule);
            } else {
                splitGroupToRules(scope, scope.rules[0] as IGroup);
            }
            return true;
        }

        for (const rule of scope.rules) {
            if (isGroup(rule) && findSingleChildGroups(rule)) {
                return true;
            }
        }
        return false;
    }

    function updateRules({ removed }: { removed: { oldIndex: number; element: IScope; }; }) {
        if (removed === undefined) {
            return;
        }

        return findSingleChildGroups(rawQuery);
    }


    function adjustValues(scope: IScope) {
        if (isGroup(scope)) {
            scope.rules.forEach(rule => adjustValues(rule));
        } else if (isRule(scope)) {
            if (scope.criterion === 'is_empty_or_null' || scope.criterion === 'is_not_empty_or_null') {
                scope.value = null;
            }
        }
    }

    function deepCopy(obj: IScope) {
        return JSON.parse(JSON.stringify(obj));
    }

    function initFilters(newFilters: IScope) {
        if (isGroup(newFilters)) {
            (rawQuery as IGroup).rules = newFilters.rules.map<IScope>(rule => deepCopy(rule));
        } else {
            (rawQuery as IGroup).rules = [];
        }
    }

    function parseRawQuery(){
        const queryCopy = deepCopy(toRaw(rawQuery));
        adjustValues(queryCopy);

        return queryCopy;
    };

    return {
        addRule,
        addFavoriteRule,
        addInnerRule,
        deleteRule,
        updateRules,
        initFilters,
        parseRawQuery,
        rawQuery
    };
}
